/**
 * The two dialogs that change client-facing figures.
 *
 * ApplyCorrectionDialog shows what will change (and what won't), the client
 * report pages that will show it, any applied correction it supersedes, and
 * then asks the reviewer to TYPE the net change, sign included. The server
 * recomputes the net change and line count inside the apply transaction and
 * refuses a mismatch (409 `delta_mismatch`), so a stale screen can never apply.
 *
 * RevertCorrectionDialog needs a reason and an explicit acknowledgement.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Spinner } from '../../ui';
import {
  applyEarningsCorrection,
  getEarningsCorrectionImpact,
  listEarningsCorrections,
  revertEarningsCorrection,
  CorrectionImpact,
  CorrectionListItem,
  CorrectionLiveView,
  EarningsCorrection,
} from '../../../services/earningsCorrectionsApi';
import {
  correctionErrorMessage,
  formatSignedCents,
  monthLabel,
  monthRangeLabel,
  parseMoneyToCents,
  typedAmountMatches,
} from './correctionModel';
import { cs, deltaColor, noticeStyle } from './correctionStyles';

/** Codes that mean the screen is out of date — reload the correction and review again. */
const STALE_CODES = new Set(['base_drift', 'delta_mismatch', 'unresolved', 'month_missing', 'not_draft', 'overlap', 'invalid_supersede']);

const HIDDEN_REASON: Record<NonNullable<CorrectionImpact['pages'][number]['hiddenReason']>, string> = {
  outside_window: 'outside the page’s 12-month window',
  adsense_hidden: 'the page hides AdSense',
};

function useImpact(id: string) {
  const [impact, setImpact] = useState<CorrectionImpact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setError(null);
    getEarningsCorrectionImpact(id)
      .then((res) => {
        if (!cancelled) setImpact(res);
      })
      .catch((err) => {
        if (!cancelled) setError(correctionErrorMessage(err, 'Could not load the affected client pages.'));
      });
    return () => {
      cancelled = true;
    };
  }, [id, attempt]);
  return { impact, error, retry: () => setAttempt((a) => a + 1) };
}

const ImpactLists: React.FC<{ impact: CorrectionImpact; reverting?: boolean }> = ({ impact, reverting }) => {
  const shown = impact.pages.filter((p) => p.visible);
  const hidden = impact.pages.filter((p) => !p.visible);
  return (
    <>
      <div style={cs.kvGrid}>
        <div>
          <div style={cs.subheading}>{reverting ? 'Goes back to ChannelMeter figures' : 'Shows the corrected figures'}</div>
          <ul style={cs.list}>
            {impact.changes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
        <div>
          <div style={cs.subheading}>Never changes</div>
          <ul style={cs.list}>
            {impact.unchanged.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      </div>
      <div>
        <div style={cs.subheading}>
          Client report pages ({monthLabel(impact.window.fromMonth)} – {monthLabel(impact.window.toMonth)} window)
        </div>
        {impact.pages.length === 0 ? (
          <div style={cs.small}>No client report page includes these channels.</div>
        ) : (
          <ul style={cs.list}>
            {shown.map((p) => (
              <li key={p.slug}>
                <strong>{p.clientLabel}</strong> <span style={cs.muted}>({p.pageType}, /{p.slug})</span>:{' '}
                {reverting ? 'drops the “Updated” marker for' : 'marks'} {monthRangeLabel(p.months)} “Updated”
                {reverting ? '' : ' with a footnote'}
              </li>
            ))}
            {hidden.map((p) => (
              <li key={p.slug} style={cs.muted}>
                {p.clientLabel} ({p.pageType}, /{p.slug}): no visible change — {p.hiddenReason ? HIDDEN_REASON[p.hiddenReason] : 'not shown'}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

interface ApplyCorrectionDialogProps {
  correction: EarningsCorrection;
  live: CorrectionLiveView;
  onClose: () => void;
  onApplied: (result: { correction: EarningsCorrection; version: number; superseded: string[] }) => void;
  /** The server says this screen is out of date (drift, mismatch, overlap…). */
  onStale: () => void;
}

const ApplyCorrectionDialog: React.FC<ApplyCorrectionDialogProps> = ({ correction, live, onClose, onApplied, onStale }) => {
  const { impact, error: impactError, retry } = useImpact(correction.id);
  const [applied, setApplied] = useState<CorrectionListItem[] | null>(live.overlaps.length ? null : []);
  const [typed, setTyped] = useState('');
  const [supersedeAck, setSupersedeAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; stale: boolean } | null>(null);

  const overlapIds = useMemo(() => live.overlaps.map((o) => o.correctionId), [live.overlaps]);

  useEffect(() => {
    if (!overlapIds.length) return undefined;
    let cancelled = false;
    listEarningsCorrections({ status: 'applied' })
      .then(({ corrections }) => {
        if (!cancelled) setApplied(corrections.filter((c) => overlapIds.includes(c.id)));
      })
      .catch(() => {
        if (!cancelled) setApplied([]);
      });
    return () => {
      cancelled = true;
    };
  }, [overlapIds]);

  const expected = live.netDeltaCents.total;
  const matches = typedAmountMatches(typed, expected);
  const flaggedLines = correction.lines.filter((l) => l.note?.startsWith('proposal flags:')).length;
  const canApply = matches && !busy && impact !== null && (overlapIds.length === 0 || supersedeAck);

  const apply = async () => {
    const confirmNetDeltaCents = parseMoneyToCents(typed);
    if (confirmNetDeltaCents == null) return;
    setBusy(true);
    setError(null);
    try {
      const result = await applyEarningsCorrection(correction.id, {
        confirmNetDeltaCents,
        expectedLineCount: live.lineCount,
        ...(overlapIds.length ? { supersedes: overlapIds } : {}),
      });
      onApplied(result);
    } catch (err) {
      const code = (err as { code?: string } | null)?.code;
      setError({ message: correctionErrorMessage(err, 'Failed to apply the correction.'), stale: !!code && STALE_CODES.has(code) });
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      eyebrow="Earnings Corrections"
      title="Apply correction"
      width={720}
      closeOnBackdrop={false}
      closeOnEsc={!busy}
      footer={
        <div style={{ ...cs.row, justifyContent: 'flex-end', width: '100%' }}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={apply} disabled={!canApply}>
            {busy ? 'Applying…' : 'Apply correction'}
          </Button>
        </div>
      }
    >
      <div style={cs.stack}>
        <div>
          <div style={{ fontWeight: 600 }}>{correction.title}</div>
          <div style={cs.small}>
            {monthRangeLabel(correction.months)} · {live.lineCount} line{live.lineCount === 1 ? '' : 's'} · {correction.channelIds.length}{' '}
            channel{correction.channelIds.length === 1 ? '' : 's'}
          </div>
          <div style={{ marginTop: 8, fontSize: 13 }}>
            Net change to creator earnings vs ChannelMeter:{' '}
            <strong style={{ color: deltaColor(expected) }}>{formatSignedCents(expected)}</strong>{' '}
            <span style={cs.muted}>
              (Direct Sold {formatSignedCents(live.netDeltaCents.directSold)} · AdSense {formatSignedCents(live.netDeltaCents.adSense)})
            </span>
          </div>
          {flaggedLines > 0 && (
            <div style={cs.help}>{flaggedLines} line(s) carry proposal flags. Their notes list which ones.</div>
          )}
        </div>

        <div style={noticeStyle('warning')}>
          <strong>ChannelMeter won’t pay or claw back this difference.</strong> Its creator emails and the payout sheets keep
          ChannelMeter’s amounts. Settle the {formatSignedCents(expected)} outside ChannelMeter (Gusto, wire or invoice), then
          record it under Settlement on this correction.
        </div>

        {impactError ? (
          <div style={noticeStyle('danger')}>
            {impactError}{' '}
            <button type="button" style={cs.linkButton} onClick={retry}>
              Try again
            </button>
          </div>
        ) : impact ? (
          <ImpactLists impact={impact} />
        ) : (
          <div style={cs.loadingRow}>
            <Spinner label="Checking affected client pages…" />
          </div>
        )}

        {overlapIds.length > 0 && (
          <div style={noticeStyle('info')}>
            <strong>This supersedes an applied correction.</strong> Every line of the superseded correction stops applying,
            not just the overlapping rows. Make sure this correction includes any of its lines you still need.
            <ul style={cs.list}>
              {live.overlaps.map((o) => {
                const c = applied?.find((a) => a.id === o.correctionId);
                return (
                  <li key={o.correctionId}>
                    {c ? `“${c.title}”` : `Correction ${o.correctionId}`}: {o.rows} overlapping row(s)
                    {c ? ` of ${c.summary.lineCount}, net ${formatSignedCents(c.summary.netDeltaCents.total)}` : ''}
                  </li>
                );
              })}
            </ul>
            <label style={{ ...cs.checkboxLabel, marginTop: 6 }}>
              <input type="checkbox" checked={supersedeAck} onChange={(e) => setSupersedeAck(e.target.checked)} />
              Supersede {overlapIds.length === 1 ? 'it' : 'them'}
            </label>
          </div>
        )}

        <div>
          <label style={cs.label} htmlFor="apply-confirm-amount">
            Type the net change to confirm
          </label>
          <div style={cs.row}>
            <input
              id="apply-confirm-amount"
              style={{ ...cs.input, width: 220 }}
              value={typed}
              placeholder={formatSignedCents(expected)}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              autoFocus
            />
            <code style={cs.mono}>{formatSignedCents(expected)}</code>
          </div>
          <div style={cs.help}>
            {typed && !matches
              ? parseMoneyToCents(typed) === -expected && expected !== 0
                ? 'Check the sign: this correction moves earnings the other way.'
                : 'Doesn’t match yet.'
              : 'Include the sign. It confirms which way creator earnings move.'}
          </div>
        </div>

        {error && (
          <div style={noticeStyle('danger')} role="alert">
            {error.message}
            {error.stale && (
              <>
                {' '}
                <button type="button" style={cs.linkButton} onClick={onStale}>
                  Reload the correction
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Revert
// ---------------------------------------------------------------------------

interface RevertCorrectionDialogProps {
  correction: EarningsCorrection;
  onClose: () => void;
  onReverted: () => void;
}

export const RevertCorrectionDialog: React.FC<RevertCorrectionDialogProps> = ({ correction, onClose, onReverted }) => {
  const { impact, error: impactError, retry } = useImpact(correction.id);
  const [reason, setReason] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const net = correction.summary.netDeltaCents.total;
  const canRevert = reason.trim().length >= 3 && ack && !busy;

  const revert = async () => {
    setBusy(true);
    setError(null);
    try {
      await revertEarningsCorrection(correction.id, reason.trim());
      onReverted();
    } catch (err) {
      setError(correctionErrorMessage(err, 'Failed to revert the correction.'));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      eyebrow="Earnings Corrections"
      title="Revert correction"
      width={640}
      closeOnBackdrop={false}
      closeOnEsc={!busy}
      footer={
        <div style={{ ...cs.row, justifyContent: 'flex-end', width: '100%' }}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={revert} disabled={!canRevert}>
            {busy ? 'Reverting…' : 'Revert correction'}
          </Button>
        </div>
      }
    >
      <div style={cs.stack}>
        <div>
          <div style={{ fontWeight: 600 }}>{correction.title}</div>
          <div style={cs.small}>
            {monthRangeLabel(correction.months)} · {correction.summary.lineCount} line(s) · net {formatSignedCents(net)}
          </div>
        </div>
        <div style={noticeStyle('warning')}>
          Every surface goes back to ChannelMeter’s figures for these rows. A settlement that was already paid is not undone,
          so say in the reason how it will be handled.
        </div>
        {impactError ? (
          <div style={noticeStyle('danger')}>
            {impactError}{' '}
            <button type="button" style={cs.linkButton} onClick={retry}>
              Try again
            </button>
          </div>
        ) : impact ? (
          <ImpactLists impact={impact} reverting />
        ) : (
          <div style={cs.loadingRow}>
            <Spinner label="Checking affected client pages…" />
          </div>
        )}
        <div>
          <label style={cs.label} htmlFor="revert-reason">
            Reason (internal)
          </label>
          <textarea
            id="revert-reason"
            style={cs.textarea}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why this correction is being rolled back"
          />
        </div>
        <label style={cs.checkboxLabel}>
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          Show ChannelMeter’s figures again on the Earnings tab, in chat and on client pages
        </label>
        {error && (
          <div style={noticeStyle('danger')} role="alert">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ApplyCorrectionDialog;
